import {
  CellClassParams,
  CellStyle,
  ColDef,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  Module,
  ProcessCellForExportParams,
  RowClassParams,
} from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  HeliosColumnTypes,
  heliosDefaultColWidth,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { NfTooltipParams } from '@northfork/ag-grid'
import {
  composeFieldPath,
  formatDate,
  ISODateTimeFormat,
} from '@northfork/basics'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { JobsKey } from '../../../../panel-key'
import {
  BulkSplitRow,
  expandBulkCommands,
} from '../../../grid-helpers/bulk-command-split'
import { commandDataCellRenderer } from '../../../grid-helpers/command-data-cell-renderer'
import { Command } from '../../../model/job.model'
import { JobsService } from '../../../services/jobs.service'
import { JsonReaderModalComponent } from '../../json-reader-modal/json-reader-modal.component'

const CommandGridKey = `${JobsKey}-CommandGrid`

const CommandPath = composeFieldPath([] as Command[])

/**
 * Visual treatment for rows that came from a split bulk command (PVAL-25510).
 * Child rows get a row-level CSS class (styled in system-commands.component.scss
 * with !important to win against ag-Grid's per-cell painting — matching the
 * `.row-held` / `.loaded-account` pattern used elsewhere in helios-ui) and
 * the leftmost content column is indented, so auditors can tell at a glance
 * that the row is derived from the bundled parent above. The chevron alone
 * proved too subtle in practice.
 */
export const BULK_SPLIT_CHILD_ROW_CLASS = 'bulk-split-child'
const CHILD_ROW_TYPE_INDENT_PX = 24

function isChildRow(params: { node?: { level?: number } | null }): boolean {
  return (params.node?.level ?? 0) > 0
}

export function bulkSplitRowClass(params: RowClassParams): string {
  return isChildRow(params) ? BULK_SPLIT_CHILD_ROW_CLASS : ''
}

export function bulkSplitTypeCellStyle(params: CellClassParams): CellStyle {
  return isChildRow(params)
    ? { paddingLeft: `${CHILD_ROW_TYPE_INDENT_PX}px` }
    : {}
}
@Component({
  selector: 'hs-system-commands',
  templateUrl: './system-commands.component.html',
  styleUrls: ['./system-commands.component.scss'],
})
export class SystemCommandsComponent implements OnDestroy {
  /**
   * Rows fed to the grid. Bulk commands (e.g. Advance Resolve Errors) are
   * expanded into a parent + N child rows by `expandBulkCommands` so each
   * per-issue payload can be screenshotted in isolation for KPMG audit
   * tie-outs (PVAL-25510). Non-bulk commands pass through unchanged.
   */
  commands: BulkSplitRow[] = []
  gridOptions: GridOptions = this.createGridOptions()
  gridModules: Module[] = []
  /** @internal */
  gridApi?: GridApi

  private readonly destroySubject$ = new Subject<void>()
  isLoading = false

  private _jobId = [-1]

  @Input()
  set jobId(value: number[]) {
    this._jobId = value
    this.loadSystemCommands()
  }

  search = new FormControl('')
  @Output()
  selectedCommandsJobIds = new EventEmitter<string>()

  constructor(
    private readonly jobsService: JobsService,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly modalService: NgbModal,
  ) {}
  ngOnInit() {
    this.search.valueChanges.pipe(debounceTime(500)).subscribe(search => {
      this.gridApi?.setQuickFilter(search)
    })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.destroy()
  }

  loadSystemCommands() {
    this.gridModules = []
    this.gridOptions = this.createGridOptions()
    const jobIds = this._jobId.slice(0, 250)
    this.loadCommandLogs(jobIds)
  }

  async loadCommandLogs(jobIds: number[]) {
    this.isLoading = true
    const promises = jobIds.map(async jobId => {
      const commandLogs = await this.jobsService
        .getCommandLogs(jobId)
        .toPromise()
      return commandLogs.map(command => ({ ...command, jobId }))
    })
    const responses = await Promise.all(promises)
    this.commands = expandBulkCommands(responses.flat())
    this.isLoading = false
  }

  createGridOptions(): GridOptions {
    return {
      ...heliosMultiRowSelectionConfig,
      enableBrowserTooltips: true,
      columnDefs: this.createBaseColumnDefs(),
      treeData: true,
      getDataPath: (data: BulkSplitRow) => data.path,
      groupDefaultExpanded: 0,
      cacheQuickFilter: true,
      excludeChildrenWhenTreeDataFiltering: true,
      autoGroupColumnDef: {
        headerName: '',
        width: 24,
        minWidth: 24,
        maxWidth: 24,
        pinned: 'left',
        resizable: false,
        sortable: false,
        suppressMenu: true,
        cellRendererParams: { suppressCount: true },
      },
      getRowClass: bulkSplitRowClass,
      onGridReady: event => this.onGridReady(event),
      processCellForClipboard: (params: ProcessCellForExportParams) => {
        if (typeof params.value === 'object') {
          return JSON.stringify(params.value)
        }
        if (params.column.getColDef().type === 'DateTime' && params.value) {
          return formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat)
        }
        return params.value
      },
      onCellDoubleClicked: event => {
        if (event.colDef.field !== 'data') return
        this.launchJsonReaderModal(event.value)
      },
      getContextMenuItems: params => this.getContextMenuItems(params),
    }
  }

  private onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    this.savedViewService.manageSavedView(
      event,
      CommandGridKey,
      this.destroySubject$,
    )

    enableHeliosMultiRowDragSelection(this.gridApi)
    enableHeliosCopyBehavior(event.api)
  }

  createBaseColumnDefs(): ColDef[] {
    return [
      {
        field: CommandPath('type'),
        width: 400,
        cellStyle: bulkSplitTypeCellStyle,
      },
      {
        field: CommandPath('data'),
        cellRenderer: commandDataCellRenderer,
        tooltipComponentParams: {
          showTooltip: () => false,
        } as NfTooltipParams,
        tooltipValueGetter: params => {
          return (
            JSON.stringify(params.value)
              .replace(/,"/g, '\n')
              .replace(/"/g, '') ?? ''
          )
        },
        filterParams: {
          textFormatter: (params: string) => {
            return JSON.stringify(params)
          },
        },
        getQuickFilterText: params =>
          params.value == null ? '' : JSON.stringify(params.value),
      },
      { field: CommandPath('userName'), width: heliosDefaultColWidth.Id },
      { field: CommandPath('note') },
      {
        field: CommandPath('timestamp'),
        type: HeliosColumnTypes.DateTime,
        valueFormatter: params => {
          return (
            formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat) ??
            params.value
          )
        },
        width: heliosDefaultColWidth.DateTime,
      },
    ]
  }

  getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
    // select the row
    if (params.node && !params.node.isSelected()) {
      params.api.selectNode(params.node)
    }
    const selectedNodes = this.gridApi?.getSelectedNodes()
    const jobIds = selectedNodes?.map(node => node.data.jobId)
    const uniqueJobIds = Array.from(new Set(jobIds)).join(',')

    const menuItems = [
      {
        name: 'Filter on Job',
        action: () => {
          this.selectedCommandsJobIds.emit(uniqueJobIds)
        },
      },
    ]
    if (params.column?.getColId() === 'data')
      menuItems.push({
        name: 'Open in JSON Reader',
        action: () => {
          this.launchJsonReaderModal(params.value)
        },
      })
    return menuItems
  }
  launchJsonReaderModal(data: string) {
    const modalRef = this.modalService.open(JsonReaderModalComponent, {
      size: 'md',
      windowClass: 'modal-window-click-through',
      backdrop: false,
      keyboard: false,
    } as NgbModalOptions)
    modalRef.componentInstance.json =
      JSON.stringify(data)
        .replace(/,/g, ',\n')
        .replace(/{"/g, '{\n"')
        .replace(/}/g, '\n}') ?? ''
  }
}
